import React, { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import {
  Button,
  CircularProgress,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ContentCopyIcon from '@material-ui/icons/FileCopyOutlined';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import AddLinkIcon from '@material-ui/icons/LinkOutlined';
import { useTranslation } from 'react-i18next';
import {
  ApiService,
  StackFileLink as StackFileLinkModel,
} from 'services/apiService';
import { notificationActions } from 'actions/notificationActions';
import { useDispatch } from 'react-redux';
import ConfirmDialog from './ConfirmDialog';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    maxWidth: '56rem',
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  item: {
    borderTop: '1px solid rgba(40, 71, 92, 0.12)',
    paddingLeft: 0,
    paddingRight: 0,
  },
  url: {
    wordBreak: 'break-all',
  },
  muted: {
    opacity: 0.55,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
}));

interface StackFileLinksProps {
  stackId: string;
}

const StackFileLinks = ({
  stackId,
}: StackFileLinksProps): React.ReactElement => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation(['translations']);
  const [links, setLinks] = useState<StackFileLinkModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [linkToRevoke, setLinkToRevoke] = useState<StackFileLinkModel | null>(
    null,
  );

  const getDownloadUrl = (link: StackFileLinkModel): string =>
    `${window.location.origin}/${ApiService.baseUrl}/stack/${link.stackId}/files/${link.token}`;

  const loadLinks = async (): Promise<void> => {
    try {
      const response = await ApiService.getStackFileLinks(stackId);
      setLinks(response.data);
    } catch (error) {
      dispatch(notificationActions.enqueueErrorResponse(error as AxiosError));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadLinks();
  }, [stackId]);

  const handleCreate = async (): Promise<void> => {
    setIsCreating(true);
    try {
      await ApiService.generateStackFileLink(stackId);
      await loadLinks();
    } catch (error) {
      dispatch(notificationActions.enqueueErrorResponse(error as AxiosError));
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopy = async (link: StackFileLinkModel): Promise<void> => {
    await navigator.clipboard.writeText(getDownloadUrl(link));
    dispatch(notificationActions.enqueueSnackbar(t('stack_page.link_copied')));
  };

  const handleRevoke = async (): Promise<void> => {
    if (!linkToRevoke) return;
    try {
      await ApiService.revokeStackFileLink(stackId, linkToRevoke.linkId);
      setLinks((current) =>
        current.map((link) =>
          link.linkId === linkToRevoke.linkId
            ? { ...link, revoked: true }
            : link,
        ),
      );
    } catch (error) {
      dispatch(notificationActions.enqueueErrorResponse(error as AxiosError));
    } finally {
      setLinkToRevoke(null);
    }
  };

  const isExpired = (link: StackFileLinkModel): boolean =>
    new Date(link.expires).getTime() <= Date.now();

  return (
    <Paper className={classes.root} elevation={1}>
      <div className={classes.header}>
        <div>
          <Typography variant="h5">{t('stack_page.download_links')}</Typography>
          <Typography variant="body2">
            {t('stack_page.download_links_description')}
          </Typography>
        </div>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddLinkIcon />}
          onClick={handleCreate}
          disabled={isCreating}
        >
          {isCreating ? (
            <CircularProgress size={20} />
          ) : (
            t('stack_page.create_download_link')
          )}
        </Button>
      </div>
      {isLoading ? (
        <CircularProgress size={24} />
      ) : links.length === 0 ? (
        <Typography variant="body2">
          {t('stack_page.no_download_links')}
        </Typography>
      ) : (
        <List disablePadding>
          {links.map((link) => {
            const inactive = link.revoked || isExpired(link);
            return (
              <ListItem
                key={link.linkId}
                className={`${classes.item} ${inactive ? classes.muted : ''}`}
              >
                <ListItemText
                  primary={
                    <Link
                      className={classes.url}
                      href={getDownloadUrl(link)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {getDownloadUrl(link)}
                    </Link>
                  }
                  secondary={`${
                    link.revoked
                      ? t('stack_page.revoked')
                      : isExpired(link)
                      ? t('stack_page.expired')
                      : t('stack_page.expires')
                  } ${new Date(link.expires).toLocaleString()} | ${t(
                    'stack_page.created_by',
                  )}: ${link.createdBy}${
                    link.intendedFor
                      ? ` | ${t('stack_page.intended_for')}: ${
                          link.intendedFor
                        }`
                      : ''
                  }`}
                />
                <div className={classes.actions}>
                  {!inactive && (
                    <>
                      <Tooltip title={t('stack_page.open_link')}>
                        <IconButton
                          component="a"
                          href={getDownloadUrl(link)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <OpenInNewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('stack_page.copy_link')}>
                        <IconButton onClick={() => handleCopy(link)}>
                          <ContentCopyIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('stack_page.revoke_link')}>
                        <IconButton onClick={() => setLinkToRevoke(link)}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </div>
              </ListItem>
            );
          })}
        </List>
      )}
      <ConfirmDialog
        isOpen={!!linkToRevoke}
        onCancel={() => setLinkToRevoke(null)}
        onConfirm={() => handleRevoke()}
        title={t('stack_page.revoke_link')}
        text={t('stack_page.revoke_link_confirmation')}
        confirmButtonText={t('stack_page.revoke_link')}
      />
    </Paper>
  );
};

export default StackFileLinks;
